import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Building2, BookOpen, Users, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import schoolsApi from '../../api/schools';
import { useAuth } from '../../hooks/useAuth';
import Step1_BasicInfo from './steps/Step1_BasicInfo';
import Step2_Subjects from './steps/Step2_Subjects';
import Step3_Teachers from './steps/Step3_Teachers';
import Step4_Students from './steps/Step4_Students';

const STEPS = [
  { id: 1, name: 'Basic Info', key: 'basicInfo', icon: Building2 },
  { id: 2, name: 'Subjects', key: 'subjects', icon: BookOpen },
  { id: 3, name: 'Teachers', key: 'teachers', icon: Users },
  { id: 4, name: 'Students', key: 'students', icon: GraduationCap },
];

export default function SetupWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({});
  const [loading, setLoading] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null);
  const { user } = useAuth();

  const schoolId = user?.schoolId || user?.school?._id;
  const schoolType = user?.school?.type || user?.schoolType || 'school'; // Default to 'school' if not set

  // Fetch setup status on mount
  useEffect(() => {
    if (schoolId) {
      schoolsApi.getSetupStatus(schoolId)
        .then(data => setSetupStatus(data))
        .catch(err => console.error('Failed to fetch setup status:', err));
    }
  }, [schoolId]);

  // If already completed, redirect to dashboard
  useEffect(() => {
    if (setupStatus?.completed) {
      window.location.href = '/';
    }
  }, [setupStatus]);

  const handleNext = (stepData) => {
    setSetupData(prev => ({ ...prev, ...stepData }));
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleStepSubmit = async (stepKey, data) => {
    setLoading(true);
    try {
      let response;
      switch (stepKey) {
        case 'basicInfo':
          response = await schoolsApi.submitBasicInfo(schoolId, data);
          break;
        case 'subjects':
          response = await schoolsApi.submitSubjects(schoolId, data);
          break;
        case 'teachers':
          response = await schoolsApi.submitTeachers(schoolId);
          break;
        case 'students':
          response = await schoolsApi.submitStudents(schoolId);
          break;
        default:
          break;
      }
      toast.success('Step completed successfully');
      return response;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete step');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Mark setup as complete
      await schoolsApi.submitStudents(schoolId);
      toast.success('Setup completed successfully!');
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1_BasicInfo onNext={handleNext} onSubmit={(data) => handleStepSubmit('basicInfo', data)} loading={loading} schoolType={schoolType} />;
      case 2:
        return <Step2_Subjects onNext={handleNext} onBack={handleBack} onSubmit={(data) => handleStepSubmit('subjects', data)} loading={loading} />;
      case 3:
        return <Step3_Teachers onNext={handleNext} onBack={handleBack} onSubmit={() => handleStepSubmit('teachers')} loading={loading} />;
      case 4:
        return <Step4_Students onNext={handleComplete} onBack={handleBack} onSubmit={() => handleStepSubmit('students')} loading={loading} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to eduPulse</h1>
          <p className="text-gray-600">Let's set up your school in a few simple steps</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                      isActive
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : isCompleted
                        ? 'border-green-500 bg-green-500 text-white'
                        : 'border-gray-300 bg-white text-gray-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <StepIcon className="w-6 h-6" />
                    )}
                  </div>
                  <span
                    className={`mt-2 text-sm font-medium ${
                      isActive ? 'text-blue-600' : isCompleted ? 'text-green-500' : 'text-gray-400'
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
